import { LinkWrap } from '@/components/blocks/Linkable';
import { headerNavBarDropMenuItemSX } from '@/components/content/Header/styles/navBar/dropMenuItem';
import { COOKIES } from '@/data/constants/cookie';
import { CookiesSingletonContext } from '@/data/cookie/cookiesSingletonProvider';
import type { PageLink } from '@/data/Navigation';
import { getHref_Breadcrumb } from '@/utils/getHref_Breadcrumb';
import { Box, MenuItem, Stack } from '@mui/material';
import React, { useCallback, useContext, useState } from 'react';

type Props = {
	tree?: PageLink[];
	display?: 'vertical' | 'horizontal';
	level?: number;
};

export const HeaderNavBarMenuList: React.FC<Props> = ({ tree }) => {
	const { setSessionCookie } = useContext(CookiesSingletonContext);
	const [activeLevel1, setActiveLevel1] = useState<number | null>(null);
	const [activeLevel2, setActiveLevel2] = useState<number | null>(null);

	const handleClickLevel1 = useCallback(
		(index: number, trail?: string[]) => (event: React.MouseEvent<HTMLAnchorElement>) => {
			event.preventDefault(); // Don't navigate
			setSessionCookie(COOKIES.breadcrumb, trail?.length ? JSON.stringify(trail) : undefined);
			setActiveLevel1(index === activeLevel1 ? null : index);
			setActiveLevel2(null); // Reset level 2
		},
		[activeLevel1, setSessionCookie]
	);

	const handleClickLevel2 = useCallback(
		(index: number, trail?: string[]) => (event: React.MouseEvent<HTMLAnchorElement>) => {
			event.preventDefault(); // Don't navigate
			setSessionCookie(COOKIES.breadcrumb, trail?.length ? JSON.stringify(trail) : undefined);
			setActiveLevel2(index === activeLevel2 ? null : index);
		},
		[activeLevel2, setSessionCookie]
	);

	if (!tree || tree.length === 0) return null;

	return (
		<Stack direction="row" spacing={4}>
			{/* Level 1 */}
			<Stack direction="column" spacing={0}>
				{tree.map(({ label, url, children, trail }, i1) => (
					<Box key={`${label}-${url}-l1`}>
						<LinkWrap href={getHref_Breadcrumb(url, trail)}>
							<MenuItem
								component="a"
								onClick={handleClickLevel1(i1, trail)}
								sx={{
									p: '0px 0px', // smaller vertical/horizontal padding
									minHeight: 'auto',
									...headerNavBarDropMenuItemSX({ isParent: !!children?.length }),
								}}
							>
								{label}
							</MenuItem>
						</LinkWrap>
					</Box>
				))}
			</Stack>

			{/* Level 2 */}
			{activeLevel1 !== null && tree[activeLevel1]?.children?.length > 0 ? (
				<Stack direction="column" spacing={1}>
					{tree[activeLevel1].children.map(({ label, url, children, trail }, i2) => (
						<Box key={`${label}-${url}-l2`}>
							<LinkWrap href={getHref_Breadcrumb(url, trail)}>
								<MenuItem
									component="a"
									onClick={handleClickLevel2(i2, trail)}
									sx={headerNavBarDropMenuItemSX({ isParent: !!children?.length })}
								>
									{label}
								</MenuItem>
							</LinkWrap>

							{/* Level 3 */}
							{activeLevel2 === i2 && children?.length > 0 ? (
								<Stack direction="column" spacing={1} sx={{ pl: 2 }}>
									{children.map(({ label, url, children: level4, trail }) => (
										<Box key={`${label}-${url}-l3`}>
											<LinkWrap href={getHref_Breadcrumb(url, trail)}>
												<MenuItem
													component="a"
													sx={headerNavBarDropMenuItemSX({ isParent: !!level4?.length })}
												>
													{label}
												</MenuItem>
											</LinkWrap>
										</Box>
									))}
								</Stack>
							) : null}
						</Box>
					))}
				</Stack>
			) : null}
		</Stack>
	);
};
